/**
 * ScopePill — compact, mobile-only single-tap surface that opens a
 * bottom sheet for switching org / project / board.
 *
 * Mobile PWA polish (2026-05-20):
 *   - Replaces the three-switcher row from <ScopeBar> on mobile,
 *     reclaiming ~70px of vertical space.
 *   - Trigger pill shows current org + project + board names truncated
 *     into one line: "Default · General · Kanban ▾".
 *   - Tap opens a full-width bottom sheet with three sections stacked
 *     (Organizations / Projects / Boards). Selecting any item updates
 *     state immediately + closes the sheet.
 *
 * THE SHEET IS PORTALLED TO <body> (2026-08-01) and that is load-bearing, not
 * tidiness. It renders inside <ScopeBar>, whose root carries `backdrop-blur`.
 * A backdrop-filter establishes a containing block for `position: fixed`
 * descendants, so `fixed inset-0` resolved against the 60px-tall bar instead of
 * the viewport: the sheet's `bottom-0` sat at y=60 and its Workspaces and
 * Projects rows were laid out at NEGATIVE y — off the top of a phone screen,
 * with no scroll that could reach them. Measured at 390x844 on the shipped
 * bundle: the Workspace row's box was (0, -217, 390, 55). Tapping the pill
 * looked like nothing happened.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import * as React from 'react';

import { useActiveOrg } from '../../lib/active-org';
import { useActiveProject } from '../../lib/active-project';
import { boardsApi, boardsKeys } from '../../lib/api/boards';
import { projectsV2Api, projectsV2Keys } from '../../lib/api/projects-v2';
import { cn } from '../../lib/utils';
import { PrivatePortal } from '../ui/private-portal';

export function ScopePill({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { orgs, activeOrg, activeOrgId, setActiveOrg } = useActiveOrg();
  const { activeProjectId, setActiveProject, activeBoardId, setActiveBoard } = useActiveProject();
  const params = useParams({ strict: false }) as { boardId?: string };
  const [open, setOpen] = React.useState(false);

  const isMember = activeOrgId !== null && orgs.some((o) => o.id === activeOrgId);

  const projectsQ = useQuery({
    queryKey: activeOrgId ? projectsV2Keys.list(activeOrgId) : ['projects-v2', 'list', '_none'],
    queryFn: () => (activeOrgId ? projectsV2Api.list(activeOrgId) : Promise.resolve([])),
    enabled: !!activeOrgId && isMember,
    staleTime: 30_000,
  });
  const projects = projectsQ.data ?? [];
  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;

  const boardsQ = useQuery({
    queryKey:
      activeOrgId && activeProjectId
        ? boardsKeys.listInProject(activeOrgId, activeProjectId)
        : ['boards-v2', 'list', '_none'],
    queryFn: () =>
      activeOrgId && activeProjectId
        ? boardsApi.list(activeOrgId, activeProjectId)
        : Promise.resolve([]),
    enabled: !!activeOrgId && !!activeProjectId && isMember,
    staleTime: 30_000,
  });
  const boards = boardsQ.data ?? [];
  const currentBoardId = params.boardId ?? activeBoardId;
  const activeBoard = boards.find((b) => b.id === currentBoardId) ?? null;

  // Lock body scroll while sheet is open
  React.useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const orgLabel = activeOrg?.displayName ?? 'Workspace';
  const projectLabel = activeProject?.displayName ?? '—';
  const boardLabel = activeBoard?.displayName ?? '—';

  // P1-8 (audit 2026-05-25): three pills competing for one row used
  // to truncate all of them ("Default Wor… · Gen… · Gen…") on iPhone.
  // On <sm we show only the leaf (board if any, else project) so the
  // single visible label is actually readable. Tap still opens the
  // full org/project/board switcher.
  const leafLabel = activeBoard?.displayName ?? activeProject?.displayName ?? activeOrg?.displayName ?? '—';
  const ariaLabel = `Switch scope (current: ${orgLabel} / ${projectLabel} / ${boardLabel})`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        className={cn(
          'inline-flex min-h-9 flex-1 items-center gap-1 overflow-hidden rounded-full border border-border bg-background px-3 py-1.5 text-[13px] transition-colors hover:bg-foreground/[0.04] active:bg-foreground/[0.06]',
          className,
        )}
        data-testid="scope-pill-trigger"
      >
        {/* Compact (xs / <sm): single leaf label only — no "·" chain. */}
        <span className="truncate font-medium text-foreground sm:hidden">{leafLabel}</span>
        {/* sm+: full breadcrumb chain (room enough on iPad / large phones). */}
        <span className="hidden truncate font-medium text-foreground sm:inline">{orgLabel}</span>
        <span aria-hidden className="hidden shrink-0 text-muted-foreground/50 sm:inline">·</span>
        <span className="hidden truncate text-muted-foreground sm:inline">{projectLabel}</span>
        <span aria-hidden className="hidden shrink-0 text-muted-foreground/50 sm:inline">·</span>
        <span className="hidden truncate text-foreground/80 sm:inline">{boardLabel}</span>
        <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      {/* Portalled outside the transformed header, but inside the private veil. */}
      {open && (
        <PrivatePortal>
          <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
          />
          <div
            className={cn(
              'absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-background shadow-2xl',
              'animate-slide-up pb-safe',
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-3">
              <h2 className="text-[15px] font-semibold tracking-tight text-foreground">Switch scope</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {/* Organizations */}
            <Section title="Workspaces">
              {orgs.length === 0 ? (
                <EmptyHint>No Workspaces yet.</EmptyHint>
              ) : (
                orgs.map((o) => (
                  <Row
                    key={o.id}
                    label={o.displayName}
                    sub={o.slug}
                    icon={o.icon ?? null}
                    isActive={o.id === activeOrgId}
                    onSelect={() => {
                      setActiveOrg(o.id);
                    }}
                  />
                ))
              )}
            </Section>

            {/* Projects */}
            <Section title="Projects">
              {projects.length === 0 ? (
                <EmptyHint>No projects in this Workspace.</EmptyHint>
              ) : (
                projects.map((p) => (
                  <Row
                    key={p.id}
                    label={p.displayName}
                    isActive={p.id === activeProjectId}
                    onSelect={() => setActiveProject(p.id)}
                  />
                ))
              )}
            </Section>

            {/* Boards */}
            <Section title="Boards">
              {boards.length === 0 ? (
                <EmptyHint>No boards in this project.</EmptyHint>
              ) : (
                boards.map((b) => (
                  <Row
                    key={b.id}
                    label={b.displayName}
                    isActive={b.id === currentBoardId}
                    onSelect={() => {
                      setActiveBoard(b.id);
                      void navigate({ to: '/board/$boardId', params: { boardId: b.id } });
                      setOpen(false);
                    }}
                  />
                ))
              )}
            </Section>
          </div>
          </div>
        </PrivatePortal>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border last:border-b-0">
      <div className="px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="pb-1">{children}</div>
    </div>
  );
}

interface RowProps {
  label: string;
  sub?: string;
  icon?: string | null;
  isActive: boolean;
  onSelect: () => void;
}

/**
 * Chrome for a row in the scope bottom sheet. Shared verbatim with the
 * MobileBoardTitle sheet in scope-bar.tsx so the two cannot drift, and
 * EXPORTED so the geometry test measures the PRODUCTION string rather than a
 * copy of it that silently rots.
 *
 * `pointer-coarse:min-h-11`, not an unconditional one. This sheet is gated by
 * `useIsMobile()`, which is a VIEWPORT test -- `(max-width: 767px)` -- so a
 * mouse-driven desktop window narrower than 768px renders this row too; the
 * gate never guaranteed a finger. The registered `pointer-coarse:` variant
 * keys off the input device instead, so the 44px floor lands on the finger
 * that needs it and nowhere else. Measured: fine 41.5px / coarse 44px.
 */
export const SCOPE_SHEET_ROW_CLASS =
  'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors pointer-coarse:min-h-11';

function Row({ label, sub, icon, isActive, onSelect }: RowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        SCOPE_SHEET_ROW_CLASS,
        isActive
          ? 'bg-foreground/[0.06] text-foreground'
          : 'text-foreground hover:bg-foreground/5 active:bg-foreground/10',
      )}
    >
      {icon && (
        <span aria-hidden className="text-base leading-none">
          {icon}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-[14px] font-medium">{label}</span>
        {sub && <span className="truncate text-[11px] text-muted-foreground">{sub}</span>}
      </span>
      {isActive && <Check className="h-4 w-4 shrink-0 text-foreground" aria-hidden />}
    </button>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-3 text-[12px] text-muted-foreground">{children}</div>;
}
